// Apolo IDE — Auto PR button + confirmation modal (Sprint v11 E12 P5).
//
// Lives in the SessionWorkspace toolbar. Click prepares the PR draft (reads
// git status + diff via Electron IPC, asks LLM for commit msg + PR body),
// then shows a modal where the user can tweak the title/body before
// committing + pushing + opening the PR.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, GitPullRequest, Loader2, X } from 'lucide-react';
import { useAutoPR } from '../../hooks/useAutoPR.js';
import { hasElectronBridge } from '../../lib/electronBridge.js';

/**
 * @param {{
 *   sessionId: string,
 *   session: { directory?: string, branch?: string },
 *   variant?: 'toolbar' | 'menu',
 *   onBeforeOpen?: () => void,
 * }} props
 */
export default function AutoPRButton({ sessionId, session, variant = 'toolbar', onBeforeOpen }) {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const dialogRef = useRef(null);

  const auto = useAutoPR({
    sessionId,
    root: session?.directory || '',
    branch: session?.branch || 'feature',
    base: 'main',
    owner,
    repo,
  });

  useEffect(() => {
    if (open && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
    if (!open && dialogRef.current?.open) dialogRef.current.close();
  }, [open]);

  useEffect(() => {
    if (auto.draft) {
      setTitleDraft(auto.draft.pr_title || '');
      setBodyDraft(auto.draft.pr_body || '');
    }
  }, [auto.draft]);

  const electronOk = hasElectronBridge();
  const hasWorktree = Boolean(session?.directory);
  const disabledReason = !electronOk
    ? 'Disponível só no app Electron'
    : !hasWorktree
    ? 'Esta sessão não tem worktree git. Crie a sessão com "Worktree isolado" pra usar Auto PR.'
    : null;

  const isMenu = variant === 'menu';

  const handleOpenClick = useCallback(async () => {
    if (disabledReason) return;
    onBeforeOpen?.();
    setOpen(true);
    if (!auto.draft && auto.phase !== 'preparing') {
      await auto.prepare();
    }
  }, [auto, disabledReason, onBeforeOpen]);

  const handleConfirm = useCallback(async () => {
    await auto.confirmAndShip({ pr_title: titleDraft, pr_body: bodyDraft });
  }, [auto, titleDraft, bodyDraft]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <button
        type="button"
        role={isMenu ? 'menuitem' : undefined}
        onClick={handleOpenClick}
        disabled={!!disabledReason}
        title={disabledReason || 'Auto PR — gera commit + PR a partir do worktree'}
        aria-label="Abrir Auto PR"
        className={
          isMenu
            ? 'flex h-7 w-full items-center gap-2 rounded-md px-2 text-sm font-medium text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40'
            : 'inline-flex h-7 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] text-apolo-textSecondary transition hover:border-apolo-accent/40 hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-apolo-borderSubtle disabled:hover:bg-apolo-surface2 disabled:hover:text-apolo-textSecondary'
        }
      >
        <GitPullRequest size={isMenu ? 14 : 11} aria-hidden="true" />
        <span className={isMenu ? undefined : 'hidden lg:inline'}>Auto PR</span>
      </button>

      <dialog
        ref={dialogRef}
        aria-labelledby="apolo-auto-pr-title"
        onClose={handleClose}
        className="rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary backdrop:bg-apolo-overlay"
      >
        <div className="flex w-[min(36rem,92vw)] flex-col gap-3 p-5">
          <header className="flex items-center justify-between">
            <h2 id="apolo-auto-pr-title" className="flex items-center gap-2 text-lg font-medium">
              <GitPullRequest size={16} aria-hidden="true" />
              Auto PR
            </h2>
            <button
              type="button"
              aria-label="Fechar"
              onClick={handleClose}
              className="rounded-md p-1 text-apolo-textTertiary transition hover:text-apolo-textPrimary"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </header>

          {!electronOk && (
            <div role="alert" className="rounded-md border border-apolo-warning/40 bg-apolo-warningBg p-2 text-xs text-apolo-warning">
              Electron file bridge ausente — abra Apolo desktop pra usar git IPC.
            </div>
          )}

          {auto.phase === 'preparing' && (
            <div className="flex items-center gap-2 text-xs text-apolo-textTertiary">
              <Loader2 size={11} className="animate-spin" aria-hidden="true" />
              Lendo diff do worktree + gerando draft via DeepSeek Flash…
            </div>
          )}

          {auto.error && (
            <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-errorBg p-2 text-xs text-apolo-error">
              {auto.error}
            </div>
          )}

          {auto.draft && (
            <>
              <div>
                <label htmlFor="apolo-pr-owner-repo" className="text-[11px] font-medium text-apolo-textTertiary">
                  GitHub owner / repo
                </label>
                <div id="apolo-pr-owner-repo" className="mt-1 flex gap-2">
                  <input
                    value={owner}
                    onChange={(e) => setOwner(e.target.value.trim())}
                    placeholder="pedromleal15"
                    className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary outline-none focus:border-apolo-accent"
                  />
                  <span className="self-center text-apolo-textTertiary">/</span>
                  <input
                    value={repo}
                    onChange={(e) => setRepo(e.target.value.trim())}
                    placeholder="apolo-IDE"
                    className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary outline-none focus:border-apolo-accent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="apolo-pr-title" className="text-[11px] font-medium text-apolo-textTertiary">
                  Title (commit message)
                </label>
                <input
                  id="apolo-pr-title"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  className="mt-1 w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary outline-none focus:border-apolo-accent"
                />
              </div>

              <div>
                <label htmlFor="apolo-pr-body" className="text-[11px] font-medium text-apolo-textTertiary">
                  Body (markdown)
                </label>
                <textarea
                  id="apolo-pr-body"
                  value={bodyDraft}
                  onChange={(e) => setBodyDraft(e.target.value)}
                  rows={10}
                  className="mt-1 w-full resize-y rounded-md border border-apolo-borderSubtle bg-apolo-bg p-2 font-mono text-[11px] text-apolo-textPrimary outline-none focus:border-apolo-accent"
                />
              </div>

              {auto.draft.files?.length > 0 && (
                <details className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2 text-xs text-apolo-textSecondary">
                  <summary className="cursor-pointer select-none">
                    {auto.draft.files.length} arquivo(s) modificado(s)
                  </summary>
                  <ul className="mt-2 ml-3 list-disc text-[11px]">
                    {auto.draft.files.slice(0, 50).map((f, i) => (
                      <li key={i}><code>{f}</code></li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}

          {auto.prUrl && (
            <div role="status" className="rounded-md border border-apolo-success/40 bg-apolo-successBg p-2 text-xs text-apolo-success">
              <a href={auto.prUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                PR aberto <ExternalLink size={10} aria-hidden="true" />
              </a>
            </div>
          )}

          <footer className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-apolo-borderSubtle px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:text-apolo-textPrimary"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                !auto.draft || !titleDraft || !owner || !repo ||
                !electronOk || auto.phase === 'committing' || auto.phase === 'pushing' || auto.phase === 'opening'
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover disabled:opacity-50"
            >
              {['committing', 'pushing', 'opening'].includes(auto.phase) ? (
                <>
                  <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                  {auto.phase === 'committing' ? 'Committando…' : auto.phase === 'pushing' ? 'Pushing…' : 'Abrindo PR…'}
                </>
              ) : (
                <>
                  <GitPullRequest size={11} aria-hidden="true" />
                  Commit + Push + PR
                </>
              )}
            </button>
          </footer>
        </div>
      </dialog>
    </>
  );
}
